import { ReactNode } from "react";
import { Redirect } from "react-router-dom";
import { LoaderBar } from "@screencloud/screencloud-ui-components";
import { ROUTES } from "src/constants/routes";
import { useAppContext } from "src/hooks/useAppContext";

interface Props {
  /** The billing version this route is for. Orgs on the other version are redirected to their own billing page. */
  version: "v1" | "v2";
  children: ReactNode;
}

/**
 * Sends each org to the billing page that matches its billing version.
 *
 * @remarks
 * While the version is still loading (`null`) it shows a loader instead of guessing, so a v2 org never flashes the v1
 * page. Billing v2 errors fail open as v1 in `getBillingVersion`, so v1 orgs are never stuck here.
 */
export const BillingVersionGuard = ({ version, children }: Props) => {
  const { billingVersion } = useAppContext();

  if (billingVersion === null) {
    return <LoaderBar />;
  }
  if (billingVersion !== version) {
    return (
      <Redirect
        to={billingVersion === "v2" ? ROUTES.ACCOUNT_V2 : ROUTES.ACCOUNT_LATEST}
      />
    );
  }
  return <>{children}</>;
};
